import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { tr } from "@/utils/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Crown, Gift, Sparkles, Zap, Share2, FlaskConical, User, PauseCircle, PlayCircle, Loader2, XCircle, RotateCcw, Calendar } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import api from "@/utils/api";
import { CancelConfirmDialog } from "./CancelConfirmDialog";

const PREMIUM_FEATURES = (language) => [
  { icon: Zap, text: tr(language, 'Unlimited cycles', 'Ciclos ilimitados', 'Ciclos ilimitados') },
  { icon: Share2, text: tr(language, 'Share cycles', 'Compartilhar ciclos', 'Compartir ciclos') },
  { icon: FlaskConical, text: tr(language, 'Custom peptides', 'Peptídeos personalizados', 'Péptidos personalizados') },
  { icon: User, text: tr(language, 'Collaborators', 'Colaboradores', 'Colaboradores') },
];

const formatDate = (v) => {
  if (!v) return null;
  try { return new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return null; }
};

export function SubscriptionTab({ subscriptionStatus, language, navigate, getAuthHeader, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const t = (key) => {
    const translations = {
      pt: { plan: 'Plano', trialEnds: 'Teste termina em', trialStart: 'Teste iniciou em', renews: 'Renova em', cyclesUsed: 'Ciclos usados este mês', bonusDays: 'dias bônus disponíveis!', upgrade: 'Fazer Upgrade para Premium', premiumFeatures: 'Recursos Premium', subStatus: 'Status da Assinatura', freeTrial: '30 DIAS GRÁTIS', free: 'GRÁTIS', daysLeft: 'dias restantes', pause: 'Pausar Assinatura', resume: 'Retomar Assinatura', paused: 'PAUSADA', pauseDesc: 'Congele seu acesso. Dias restantes são preservados.', pausedNotice: 'Sua assinatura está pausada.', daysFrozen: 'dias congelados, prontos para retomar', cancel: 'Cancelar Assinatura', cancelled: 'CANCELADA', cancelledNotice: 'Sua assinatura foi cancelada.', reactivate: 'Reativar Assinatura', nextBilling: 'Próxima cobrança', memberDiscount: '30% de desconto Summit ativado' },
      es: { plan: 'Plan', trialEnds: 'Prueba termina el', trialStart: 'Prueba iniciada el', renews: 'Renueva el', cyclesUsed: 'Ciclos usados este mes', bonusDays: '¡días de bonificación disponibles!', upgrade: 'Actualizar a Premium', premiumFeatures: 'Funciones Premium', subStatus: 'Estado de Suscripción', freeTrial: '30 DÍAS GRATIS', free: 'GRATIS', daysLeft: 'días restantes', pause: 'Pausar Suscripción', resume: 'Reanudar Suscripción', paused: 'PAUSADA', pauseDesc: 'Congela tu acceso. Los días restantes se conservan.', pausedNotice: 'Tu suscripción está pausada.', daysFrozen: 'días congelados, listos para reanudar', cancel: 'Cancelar Suscripción', cancelled: 'CANCELADA', cancelledNotice: 'Tu suscripción fue cancelada.', reactivate: 'Reactivar Suscripción', nextBilling: 'Próxima facturación', memberDiscount: '30% descuento Summit activado' },
      en: { plan: 'Plan', trialEnds: 'Trial ends', trialStart: 'Trial started', renews: 'Renews', cyclesUsed: 'Cycles used this month', bonusDays: 'bonus days available!', upgrade: 'Upgrade to Premium', premiumFeatures: 'Premium Features', subStatus: 'Subscription Status', freeTrial: '30-DAY FREE', free: 'FREE', daysLeft: 'days left', pause: 'Pause Subscription', resume: 'Resume Subscription', paused: 'PAUSED', pauseDesc: 'Freeze your access. Remaining days are preserved.', pausedNotice: 'Your subscription is paused.', daysFrozen: 'days frozen, ready to resume', cancel: 'Cancel Membership', cancelled: 'CANCELLED', cancelledNotice: 'Your subscription is cancelled.', reactivate: 'Reactivate Membership', nextBilling: 'Next billing', memberDiscount: '30% Summit discount active' },
    };
    return (translations[language] || translations.en)[key] || key;
  };

  const callAction = async (path, successMsg) => {
    setBusy(true);
    try {
      const res = await api.post(path, {}, { headers: getAuthHeader() });
      toast.success(successMsg || res.data.message);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const handlePause = () => callAction('/subscription/pause');
  const handleResume = () => callAction('/subscription/resume');

  const handleConfirmCancel = async () => {
    setShowCancel(false);
    await callAction('/subscription/cancel');
  };
  const handlePauseInstead = async () => {
    setShowCancel(false);
    await handlePause();
  };
  const handleReactivate = async () => {
    if (subscriptionStatus?.is_paused) {
      handleResume();
      return;
    }
    // Cancelled → try reactivate endpoint first (works if grace period still active).
    // If grace expired, fall through to pricing page.
    setBusy(true);
    try {
      const res = await api.post('/subscription/reactivate', {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      onRefresh?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 400) {
        toast.info(typeof detail === 'string' ? detail : 'Please choose a plan to reactivate');
        navigate('/subscription');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to reactivate');
      }
    } finally {
      setBusy(false);
    }
  };

  const features = PREMIUM_FEATURES(language);
  const isPaused = subscriptionStatus?.is_paused;
  const isCancelled = subscriptionStatus?.is_cancelled;
  const isTrial = subscriptionStatus?.is_trial;
  const isPremium = subscriptionStatus?.is_premium;

  return (
    <div className="space-y-6" data-testid="subscription-content">
      <Card className="bg-zinc-950/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Crown className="w-5 h-5 text-yellow-400" />
            {t('subStatus')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {subscriptionStatus ? (
            <>
              <div className="flex items-center gap-3">
                <Badge className={
                  isPaused ? "bg-amber-500/20 text-amber-400" :
                  isCancelled ? "bg-red-500/20 text-red-400" :
                  subscriptionStatus.is_premium ? "bg-yellow-500/20 text-yellow-400" : "bg-zinc-700 text-zinc-300"
                } data-testid="subscription-status-badge">
                  {isPaused ? t('paused') :
                   isCancelled ? t('cancelled') :
                   subscriptionStatus.is_premium ? (isTrial ? t('freeTrial') : 'PREMIUM') : t('free')}
                </Badge>
                {isTrial && !isPaused && !isCancelled && (
                  <span className="text-sm text-amber-400" data-testid="trial-days-left">
                    {subscriptionStatus.trial_days_left} {t('daysLeft')}
                  </span>
                )}
              </div>

              {isPaused && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg" data-testid="paused-notice">
                  <p className="text-sm text-amber-300 font-medium">{t('pausedNotice')}</p>
                  <p className="text-xs text-zinc-400 mt-1">
                    {subscriptionStatus.days_remaining_at_pause} {t('daysFrozen')}
                  </p>
                </div>
              )}

              {isCancelled && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg" data-testid="cancelled-notice">
                  <p className="text-sm text-red-300 font-medium">{t('cancelledNotice')}</p>
                  {subscriptionStatus.expires_at && (
                    <p className="text-xs text-zinc-400 mt-1">
                      {tr(language, "Access until", "Acesso até", "Acceso hasta")}: {formatDate(subscriptionStatus.expires_at)}
                    </p>
                  )}
                </div>
              )}

              {(isPremium || isPaused || isCancelled) && (
                <div className="space-y-2" data-testid="subscription-details">
                  <p className="text-sm text-zinc-400">
                    {t('plan')}: <span className="text-white font-medium">{subscriptionStatus.plan === 'yearly' ? 'Premium Yearly' : 'Premium Monthly'}</span>
                  </p>
                  {isTrial && subscriptionStatus.trial_start && (
                    <p className="text-sm text-zinc-400 flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5" />{t('trialStart')}: <span className="text-white" data-testid="trial-start">{formatDate(subscriptionStatus.trial_start)}</span>
                    </p>
                  )}
                  {isTrial && subscriptionStatus.trial_end && (
                    <p className="text-sm text-zinc-400 flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5" />{t('trialEnds')}: <span className="text-white" data-testid="trial-end">{formatDate(subscriptionStatus.trial_end)}</span>
                    </p>
                  )}
                  {!isTrial && !isCancelled && subscriptionStatus.next_billing_date && (
                    <p className="text-sm text-zinc-400 flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5" />{t('nextBilling')}: <span className="text-white" data-testid="next-billing">{formatDate(subscriptionStatus.next_billing_date)}</span>
                    </p>
                  )}
                  {isPremium && !isPaused && !isCancelled && (
                    <p className="text-xs text-green-400 flex items-center gap-2 pt-1" data-testid="member-discount">
                      ✅ {t('memberDiscount')}
                    </p>
                  )}
                </div>
              )}

              <div className="pt-4 border-t border-zinc-800">
                <p className="text-sm text-zinc-400 mb-3">
                  {t('cyclesUsed')}: 
                  <span className="text-white ml-2">{subscriptionStatus.cycles_used_this_month} / {subscriptionStatus.is_premium ? '∞' : subscriptionStatus.cycles_limit}</span>
                </p>
                {subscriptionStatus.bonus_days_available > 0 && (
                  <p className="text-sm text-green-400 flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    {subscriptionStatus.bonus_days_available} {t('bonusDays')}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                {!isPremium && !isPaused && !isCancelled && (
                  <Button className="btn-primary" onClick={() => navigate('/subscription')} data-testid="upgrade-btn">
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('upgrade')}
                  </Button>
                )}

                {isPremium && !isPaused && !isCancelled && (
                  <>
                    <Button variant="outline" onClick={handlePause} disabled={busy} className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300" data-testid="pause-btn">
                      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PauseCircle className="w-4 h-4 mr-2" />}
                      {t('pause')}
                    </Button>
                    <Button variant="outline" onClick={() => setShowCancel(true)} disabled={busy} className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="cancel-btn">
                      <XCircle className="w-4 h-4 mr-2" />
                      {t('cancel')}
                    </Button>
                  </>
                )}

                {isPaused && (
                  <Button onClick={handleResume} disabled={busy} className="bg-gradient-to-r from-cyan-500 to-violet-500 text-white" data-testid="resume-btn">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                    {t('resume')}
                  </Button>
                )}

                {isCancelled && (
                  <Button onClick={handleReactivate} disabled={busy} className="bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold" data-testid="reactivate-btn">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {t('reactivate')}
                  </Button>
                )}
              </div>

              {isPremium && !isPaused && !isCancelled && (
                <p className="text-xs text-zinc-500 italic">{t('pauseDesc')}</p>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center py-8">
              <div className="spinner w-6 h-6" />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-zinc-950/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">{t('premiumFeatures')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature) => (
              <div key={feature.text} className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-lg">
                <feature.icon className={`w-5 h-5 ${subscriptionStatus?.is_premium ? 'text-cyan-400' : 'text-zinc-600'}`} />
                <span className={subscriptionStatus?.is_premium ? 'text-white' : 'text-zinc-500'}>{feature.text}</span>
                {subscriptionStatus?.is_premium && <Badge className="ml-auto bg-green-500/20 text-green-400 text-xs">✓</Badge>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <CancelConfirmDialog
        open={showCancel}
        onOpenChange={setShowCancel}
        language={language}
        onConfirmCancel={handleConfirmCancel}
        onPauseInstead={handlePauseInstead}
        cancelling={busy}
      />
    </div>
  );
}
